import { Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type } from 'class-transformer';
import * as t from 'io-ts';
import { validateJSON, validateJSONNow } from '../web/validation';
import { companyType, CompanyType, toCompanyType } from './company';
import { GeoCoord } from './geo-coord';
import { Entity, Locatable } from './entity';
import { getDatabaseCodeTransformer } from './transfomers/transformers';
import { RouteArrayValidator, RouteEntity } from './route';
import { Default } from '../util/decorators';
import { ObjectPreferenceArrayCodec, ObjectPreferenceEntity } from './object-preference';
import { WaterGoPreferenceEntity, WaterGoPreferenceCodec } from './water-go-preference';
import { OilGoPreferenceEntity, OilGoPreferenceCodec } from './oil-go-preference';
import { PatchValue } from './patch-value';
import { AddressCodec, AddressEntity, AddressPatchDTO } from './address';
import { siteType, SiteType, toSiteType } from './site-type';

/** Validator for a site type. */
export const SiteValidator = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    ticketNumberForced: t.boolean,
    forceTankSelection: t.boolean,
    doNotUse: t.boolean
  }),
  t.partial({
    routes: RouteArrayValidator,
    waterTypeId: t.string,
    waterTypeName: t.string,
    type: t.string,
    externalId: t.string,
    leaseId: t.string,
    leaseName: t.string,
    afe: t.string,
    afePrefix: t.string,
    afeSuffix: t.string,
    geofenceExtension: t.number,
    geofenceSiteGroupId: t.string,
    geofenceSiteGroupName: t.string,
    address: AddressCodec,
    latCoord: t.number,
    longCoord: t.number,
    barcode: t.string,
    companyId: t.string,
    companyName: t.string,
    companyType: t.string,
    companyUnitId: t.string,
    companyUnitName: t.string,
    ownerId: t.string,
    ownerName: t.string,
    ownerType: t.string,
    parentSiteId: t.string,
    parentSiteName: t.string,
    active: t.boolean,
    occupied: t.boolean,
    operatingAreaId: t.string,
    operatingAreaName: t.string,
    rrcLeaseId: t.string,
    rrcLeaseName: t.string,
    transportViaPipeline: t.boolean,
    transportOilViaPipeline: t.boolean,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    regionList: t.array(t.string),

    objectPreferences: ObjectPreferenceArrayCodec,
    waterGoPreferences: WaterGoPreferenceCodec,
    oilGoPreference: OilGoPreferenceCodec
  })
]);

/** Validator for an array of site types. */
export const SiteArrayValidator = t.array(SiteValidator);

export type Site = t.TypeOf<typeof SiteValidator>;
export type SiteArray = t.TypeOf<typeof SiteArrayValidator>;

@Exclude()
export class SiteEntity implements Entity, Locatable {
  @Expose()
  id!: string;

  @Expose()
  name!: string;

  @Expose()
  ticketNumberForced = false;

  @Expose()
  forceTankSelection = false;

  @Transform(getDatabaseCodeTransformer(toSiteType))
  @Expose()
  type?: SiteType;

  @Expose()
  externalId?: string;

  @Expose()
  leaseId?: string;

  @Expose()
  leaseName?: string;

  @Expose()
  afe?: string;

  @Expose()
  afePrefix?: string;

  @Expose()
  afeSuffix?: string;

  @Expose()
  geofenceExtension?: number;

  @Expose()
  geofenceSiteGroupId?: string;

  @Expose()
  geofenceSiteGroupName?: string;

  @Expose()
  @Type(() => AddressEntity)
  address?: AddressEntity;

  @Expose()
  latCoord?: number;

  @Expose()
  longCoord?: number;

  @Expose()
  barcode?: string;

  @Expose()
  companyId?: string;

  @Expose()
  companyName?: string;

  @Transform(getDatabaseCodeTransformer(toCompanyType))
  @Expose()
  companyType?: CompanyType;

  @Expose()
  ownerId?: string;

  @Expose()
  ownerName?: string;

  @Transform(getDatabaseCodeTransformer(toCompanyType))
  @Expose()
  ownerType?: CompanyType;

  @Expose()
  companyUnitId?: string;

  @Expose()
  companyUnitName?: string;

  @Expose()
  parentSiteId?: string;

  @Expose()
  parentSiteName?: string;

  @Expose()
  private active?: boolean;

  @Expose()
  private occupied?: boolean;

  @Expose()
  operatingAreaId?: string;

  @Expose()
  operatingAreaName?: string;

  @Expose()
  rrcLeaseId?: string;

  @Expose()
  rrcLeaseName?: string;

  @Expose()
  transportViaPipeline = false;

  @Expose()
  transportOilViaPipeline = false;

  @Expose()
  doNotUse = false;

  @Expose({ name: 'regionList' })
  regionIds?: string[];

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose({ name: 'waterTypeId' })
  freightTypeId?: string;

  @Expose({ name: 'waterTypeName' })
  freightTypeName?: string;

  @Expose()
  @Type(() => RouteEntity)
  @Default([])
  routes: RouteEntity[] = [];

  @Expose()
  @Type(() => ObjectPreferenceEntity)
  objectPreferences?: ObjectPreferenceEntity[];

  @Expose()
  @Type(() => WaterGoPreferenceEntity)
  waterGoPreference?: WaterGoPreferenceEntity;

  @Expose()
  @Type(() => OilGoPreferenceEntity)
  oilGoPreferenceEntity?: OilGoPreferenceEntity;

  readonly entityType = 'SITE';

  get displayName(): Readonly<string> {
    return this.name;
  }

  get latLng(): Readonly<GeoCoord | undefined> {
    return this.location;
  }

  /**
   * Constructor for SiteEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a SiteEntity from a Site type value.
   *
   * @param site The site type value.
   */
  static from(site: Site): SiteEntity {
    return plainToInstance(SiteEntity, site, {
      excludeExtraneousValues: true
    });
  }

  loadTypeValue(): Promise<Site> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSON(SiteValidator, object);
  }

  asTypeValue(): Site {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSONNow(SiteValidator, object);
  }

  get typeWithCompanyFallback(): SiteType {
    if (this.type) {
      return this.type;
    } else if (this.companyType) {
      if (this.companyType === companyType.truckCompany) {
        return siteType.yard;
      } else if (this.companyType === companyType.vendor) {
        return siteType.disposal;
      } else {
        return siteType.pad;
      }
    } else {
      return siteType.pad;
    }
  }

  get isActive(): boolean {
    return this.active || false;
  }

  setActive(value: boolean): void {
    this.active = value;
  }

  get isOccupied(): boolean {
    return this.occupied || false;
  }

  setOccupied(value: boolean): void {
    this.occupied = value;
  }

  get location(): GeoCoord | undefined {
    /* istanbul ignore next */
    if (this.latCoord && this.longCoord) {
      return {
        latCoord: this.latCoord,
        longCoord: this.longCoord
      };
    } else {
      return undefined;
    }
  }

  set location(coordinates: GeoCoord | undefined) {
    this.latCoord = coordinates ? coordinates.latCoord : undefined;
    this.longCoord = coordinates ? coordinates.longCoord : undefined;
  }

  get abacAttributes(): Record<string, any> | undefined {
    return {
      id: this.id,
      name: this.name,
      type: this.type?.code,
      companyId: this.companyId,
      companyName: this.companyName
    };
  }
}

export const SiteHistoricalLoadForecastDataValidator = t.type({
  dttm: t.number,
  forecast: t.number,
  lowerForecast: t.number,
  upperForecast: t.number
});

export const SiteHistoricalLoadForecastDataArrayValidator = t.array(
  SiteHistoricalLoadForecastDataValidator
);

export type SiteHistoricalLoadForecastData = t.TypeOf<
  typeof SiteHistoricalLoadForecastDataValidator
>;
export type SiteHistoricalLoadForecastDataArray = t.TypeOf<
  typeof SiteHistoricalLoadForecastDataArrayValidator
>;

export const SiteHistoricalLoadForecastValidator = t.intersection([
  t.type({
    id: t.string
  }),
  t.partial({
    name: t.string,
    data: SiteHistoricalLoadForecastDataArrayValidator
  })
]);

export const SiteHistoricalLoadForecastArrayValidator = t.array(
  SiteHistoricalLoadForecastValidator
);

export type SiteHistoricalLoadForecast = t.TypeOf<
  typeof SiteHistoricalLoadForecastValidator
>;
export type SiteHistoricalLoadForecastArray = t.TypeOf<
  typeof SiteHistoricalLoadForecastArrayValidator
>;

export const SiteFillRateForecastDataValidator = t.type({
  dttm: t.number,
  forecast: t.number
});

export const SiteFillRateForecastDataArrayValidator = t.array(
  SiteFillRateForecastDataValidator
);

export type SiteFillRateForecastData = t.TypeOf<
  typeof SiteFillRateForecastDataValidator
>;
export type SiteFillRateForecastDataArray = t.TypeOf<
  typeof SiteFillRateForecastDataArrayValidator
>;

export const SiteFillRateForecastValidator = t.intersection([
  t.type({
    id: t.string
  }),
  t.partial({
    name: t.string,
    fillRate: t.number,
    data: SiteFillRateForecastDataArrayValidator
  })
]);

export const SiteFillRateForecastArrayValidator = t.array(
  SiteFillRateForecastValidator
);

export type SiteFillRateForecast = t.TypeOf<
  typeof SiteFillRateForecastValidator
>;
export type SiteFillRateForecastArray = t.TypeOf<
  typeof SiteFillRateForecastArrayValidator
>;


export interface SiteSearchCriteria {
  ids?: string[];
  regionIds?: string[];
  companyIds?: string[];
  operatingAreaIds?: string[];
  routeIds?: string[];
  routeEffectiveDttm?: number;
  geofenceSiteGroupIds?: string[];
}

export const SITE_PATCH_FIELD_MAP: Record<string, keyof SitePatchDTO> = {
  name: 'name',
  leaseId: 'leaseId',
  leaseName: 'leaseName',
  type: 'type',
  afe: 'afe',
  afePrefix: 'afePrefix',
  afeSuffix: 'afeSuffix',
  latCoord: 'latCoord',
  longCoord: 'longCoord',
  address: 'address',
  barcode: 'barcode',
  ownerId: 'ownerId',
  companyId: 'companyId',
  waterTypeId: 'freightTypeId', // remap
  parentSiteId: 'parentSiteId',
  operatingAreaId: 'operatingAreaId',
  companyUnitId: 'companyUnitId',
  geofenceSiteGroupId: 'geofenceSiteGroupId',
  transportViaPipeline: 'transportViaPipeline',
  transportOilViaPipeline: 'transportOilViaPipeline',
  doNotUse: 'doNotUse'
};

export interface SitePatchDTO {
  name?: PatchValue<string>;
  leaseId?: PatchValue<string>;
  leaseName?: PatchValue<string>;
  type?: PatchValue<string>;
  afe?: PatchValue<string>;
  afePrefix?: PatchValue<string>;
  afeSuffix?: PatchValue<string>;
  latCoord?: PatchValue<number>;
  longCoord?: PatchValue<number>;
  address?: AddressPatchDTO;
  barcode?: PatchValue<string>;
  ownerId?: PatchValue<string>;
  companyId?: PatchValue<string>;
  freightTypeId?: PatchValue<string>;
  parentSiteId?: PatchValue<string>;
  operatingAreaId?: PatchValue<string>;
  companyUnitId?: PatchValue<string>;
  geofenceSiteGroupId?: PatchValue<string>;
  transportViaPipeline?: PatchValue<boolean>;
  transportOilViaPipeline?: PatchValue<boolean>;
  doNotUse?: PatchValue<boolean>;
}

export type SiteCountiesRequest = {
  regionId: string;
  companyId?: string;
  state?: string;
}
